import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Mic, MicOff, Loader } from 'lucide-react';

const VoiceInput = ({ onResult, placeholder }) => {
  const { t, i18n } = useTranslation();
  const [listening, setListening] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [text, setText] = useState('');
  const recognitionRef = useRef(null);

  const isSwahili = i18n.language === 'sw';

  const startListening = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert(isSwahili ? 'Kifaa chako hakiungi mkono wa sauti' : 'Your device does not support voice input');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.lang = isSwahili ? 'sw-TZ' : 'en-US';
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onstart = () => {
      setListening(true);
      setText(isSwahili ? 'Nakusikiliza...' : 'Listening...');
    };

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setText(transcript);
      setProcessing(true);
      
      // Process the voice command
      setTimeout(() => {
        onResult(transcript);
        setProcessing(false);
        setText('');
      }, 500);
    };

    recognition.onerror = (event) => {
      console.error('Voice error:', event.error);
      setListening(false);
      setText(isSwahili ? 'Samahani, sikuelewa. Jaribu tena.' : 'Sorry, I did not understand. Try again.');
      setTimeout(() => setText(''), 3000);
    };

    recognition.onend = () => {
      setListening(false);
    };

    recognitionRef.current = recognition;
    recognition.start();
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setListening(false);
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={listening ? stopListening : startListening}
        disabled={processing}
        className={`p-3 rounded-xl transition-all duration-200 flex items-center gap-2 ${
          listening 
            ? 'bg-red-500/20 text-red-400 animate-pulse border border-red-500/30' 
            : processing
              ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
              : 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 border border-emerald-500/20'
        }`}
        title={isSwahili ? 'Bonyeza kuzungumza' : 'Click to speak'}
      >
        {processing ? (
          <Loader size={18} className="animate-spin" />
        ) : listening ? (
          <MicOff size={18} />
        ) : (
          <Mic size={18} />
        )}
        <span className="text-xs font-medium hidden sm:inline">
          {listening ? (isSwahili ? 'Sikiliza...' : 'Listening...') : 
           processing ? (isSwahili ? 'Inachakata...' : 'Processing...') :
           (isSwahili ? 'Sema' : 'Speak')}
        </span>
      </button>
      
      {text && (
        <div className="absolute top-full mt-2 left-0 bg-[#111a14] border border-white/10 rounded-lg px-3 py-2 text-white text-xs whitespace-nowrap z-50 shadow-xl">
          {text}
        </div>
      )}
    </div>
  );
};

export default VoiceInput;
